/**
 * Nepal flag rendered as an inline SVG so it displays consistently across
 * all platforms (Windows does not render the regional-indicator emoji 🇳🇵
 * as a flag glyph).
 *
 * Geometry follows the official 1962 Nepal Flag Construction Act, simplified
 * to a 75×100 viewBox. Crimson body with deep blue border, white sun and
 * crescent.
 */
export interface NepalFlagProps {
  /** Display height in pixels. Width is computed from the official aspect ratio. */
  size?: number;
  className?: string;
  ariaLabel?: string;
}

export default function NepalFlag({
  size = 36,
  className,
  ariaLabel = 'Flag of Nepal',
}: NepalFlagProps) {
  // Official aspect ratio is approximately 0.75 (width:height).
  const width = Math.round(size * 0.75);

  return (
    <svg
      role="img"
      aria-label={ariaLabel}
      className={className}
      width={width}
      height={size}
      viewBox="0 0 75 100"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Blue border = full pennant outline */}
      <path
        d="M0,0 L60,40 L25,40 L60,80 L0,80 Z"
        fill="#003893"
      />
      {/* Crimson interior = inset by 5 units */}
      <path
        d="M5,5 L51,42 L23,42 L51,75 L5,75 Z"
        fill="#DC143C"
      />
      {/* Crescent moon (upper triangle) */}
      <g transform="translate(20,20)" fill="#FFFFFF">
        <circle cx="0" cy="0" r="6" />
        <circle cx="2" cy="-1.5" r="5" fill="#DC143C" />
        {/* Six-point moon decoration */}
        <g transform="translate(-0.5,1)" fontSize="0">
          <circle cx="0" cy="-3" r="0.6" />
          <circle cx="2.6" cy="-1.5" r="0.6" />
          <circle cx="2.6" cy="1.5" r="0.6" />
          <circle cx="0" cy="3" r="0.6" />
          <circle cx="-2.6" cy="1.5" r="0.6" />
          <circle cx="-2.6" cy="-1.5" r="0.6" />
        </g>
      </g>
      {/* Sun (lower triangle) — twelve-ray star */}
      <g transform="translate(18,55)" fill="#FFFFFF">
        <circle cx="0" cy="0" r="3.5" />
        {Array.from({ length: 12 }).map((_, i) => {
          const angle = (i * Math.PI * 2) / 12;
          const x1 = Math.cos(angle) * 3.5;
          const y1 = Math.sin(angle) * 3.5;
          const x2 = Math.cos(angle) * 6;
          const y2 = Math.sin(angle) * 6;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="#FFFFFF"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          );
        })}
      </g>
    </svg>
  );
}
